import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash, Copy, Check, Tag, X, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const SCOPE_LABELS = {
  any: "Any purchase",
  web_project: "Web project only",
  points_package: "Points package only",
  custom_charge: "Custom invoice only",
};

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [scopes, setScopes] = useState(["any"]);
  const [stats, setStats] = useState({}); // {CODE: [count_d0, count_d1, ...]}
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // doc or null
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get("/admin/promo-codes"),
        api.get("/admin/promo-codes/stats?days=30").catch(() => ({ data: { by_code: {} } })),
      ]);
      setCodes(listRes.data.codes || []);
      setScopes(listRes.data.scopes || ["any"]);
      setStats(statsRes.data?.by_code || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setModalOpen(true); };

  const remove = async (c) => {
    if (c.is_legacy) return toast.error("Built-in codes can't be deleted");
    if (!window.confirm(`Delete code "${c.code}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${c.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleActive = async (c) => {
    if (c.is_legacy) return;
    try {
      await api.patch(`/admin/promo-codes/${c.id}`, { active: !c.active });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const copy = async (c) => {
    try {
      await navigator.clipboard.writeText(c.code);
      setCopiedId(c.code);
      toast.success("Code copied");
      setTimeout(() => setCopiedId(null), 1600);
    } catch (_) {
      window.prompt("Copy this code:", c.code);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Discounts</div>
          <h1 className="font-display text-5xl mt-2">Promo codes</h1>
          <p className="text-sm text-[#a3a3a3] mt-2 max-w-2xl">
            Create discount codes scoped to any purchase, only web projects, only point packages,
            or only custom invoices. Optional expiry and redemption limits.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="open-create-promo"
          className="btn-primary px-4 py-2.5 text-xs uppercase tracking-wider flex items-center gap-2 self-start"
        >
          <Plus size={12} weight="bold" /> New code
        </button>
      </div>

      {loading ? (
        <div className="text-[#a3a3a3] text-sm">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="border border-[#262626] bg-[#0a0a0a] p-10 text-center">
          <Tag size={32} className="text-[#262626] mx-auto" />
          <div className="font-display text-2xl mt-4">No promo codes yet</div>
          <div className="text-sm text-[#a3a3a3] mt-2">Click "New code" to create your first.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-px bg-[#262626] border border-[#262626]" data-testid="promo-list">
          {codes.map((c) => (
            <div
              key={c.code}
              className={`bg-[#0a0a0a] p-5 group hover:bg-[#0f0f0f] transition-colors ${!c.active && !c.is_legacy ? "opacity-60" : ""}`}
              data-testid={`promo-row-${c.code}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                <div className="lg:col-span-4 flex items-center gap-3 min-w-0">
                  <button
                    type="button"
                    onClick={() => copy(c)}
                    className="font-mono font-bold text-lg text-[#FF2D2D] flex items-center gap-2 hover:underline truncate"
                    title="Click to copy"
                  >
                    {c.code}
                    {copiedId === c.code ? <Check size={12} weight="bold" /> : <Copy size={12} className="opacity-60" />}
                  </button>
                  {c.is_legacy && (
                    <span className="text-[9px] uppercase tracking-wider border border-[#262626] px-1.5 py-0.5 text-[#a3a3a3] flex items-center gap-1">
                      <Lock size={10} /> Built-in
                    </span>
                  )}
                </div>
                <div className="lg:col-span-2 text-2xl font-display tabular-nums">
                  {c.discount_type === "flat"
                    ? <>$<span>{Number(c.discount).toFixed(c.discount % 1 === 0 ? 0 : 2)}</span></>
                    : <>{Math.round(c.discount * 100)}<span className="text-sm">%</span></>
                  }
                  <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3] mt-0.5">off</div>
                </div>
                <div className="lg:col-span-3 text-xs">
                  <div className="text-white">{SCOPE_LABELS[c.scope] || c.scope}</div>
                  {c.description && (
                    <div className="text-[#a3a3a3] mt-1 truncate" title={c.description}>{c.description}</div>
                  )}
                </div>
                <div className="lg:col-span-2 text-xs">
                  <Sparkline values={stats[c.code] || []} testId={`sparkline-${c.code}`} />
                  {c.expires_at && (
                    <div className="text-[#a3a3a3] mt-1">Exp {new Date(c.expires_at).toLocaleDateString()}</div>
                  )}
                  {c.max_uses != null && (
                    <div className="text-[#a3a3a3]">
                      {c.uses_count || 0} / {c.max_uses} used
                    </div>
                  )}
                  {c.max_uses == null && c.uses_count != null && (
                    <div className="text-[#a3a3a3]">{c.uses_count} used</div>
                  )}
                </div>
                <div className="lg:col-span-1 flex gap-2 justify-end">
                  {!c.is_legacy ? (
                    <>
                      <label className="inline-flex items-center cursor-pointer" title={c.active ? "Active — click to disable" : "Disabled — click to enable"}>
                        <input
                          type="checkbox"
                          checked={c.active}
                          onChange={() => toggleActive(c)}
                          data-testid={`promo-active-${c.code}`}
                          className="accent-[#FF2D2D]"
                        />
                      </label>
                      <button
                        onClick={() => openEdit(c)}
                        data-testid={`promo-edit-${c.code}`}
                        className="text-xs text-[#a3a3a3] hover:text-white px-1"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(c)}
                        data-testid={`promo-delete-${c.code}`}
                        className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]"
                        title="Delete"
                      >
                        <Trash size={13} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => openEdit(c)}
                      data-testid={`promo-view-${c.code}`}
                      className="text-xs text-[#a3a3a3] hover:text-white px-1"
                      title="View redemptions"
                    >
                      View
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PromoCodeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        scopes={scopes}
        editing={editing}
      />
    </AdminLayout>
  );
}


function PromoCodeModal({ open, onClose, onSaved, scopes, editing }) {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [redemptions, setRedemptions] = useState(null);

  function blank() {
    return { code: "", discount: 10, discount_type: "percent", scope: "any", description: "", active: true, expires_at: "", max_uses: "" };
  }

  useEffect(() => {
    if (!open) {
      setRedemptions(null);
      return;
    }
    if (editing) {
      const isFlat = editing.discount_type === "flat";
      setForm({
        code: editing.code,
        discount: isFlat ? editing.discount : Math.round((editing.discount || 0) * 100),
        discount_type: editing.discount_type || "percent",
        scope: editing.scope || "any",
        description: editing.description || "",
        active: editing.active !== false,
        expires_at: editing.expires_at ? editing.expires_at.slice(0, 10) : "",
        max_uses: editing.max_uses != null ? String(editing.max_uses) : "",
      });
      // Pull recent redemptions (use the id for DB codes, the code name for legacy)
      const key = editing.id || editing.code;
      api.get(`/admin/promo-codes/${encodeURIComponent(key)}/redemptions?limit=10`)
        .then(({ data }) => setRedemptions(data))
        .catch(() => setRedemptions({ redemptions: [], total_discounted_completed: 0 }));
    } else {
      setForm(blank());
    }
  }, [open, editing]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLegacy = editing?.is_legacy === true;

  const submit = async (e) => {
    e.preventDefault();
    if (isLegacy) {
      onClose();
      return;
    }
    const num = parseFloat(form.discount);
    if (form.discount_type === "percent") {
      if (Number.isNaN(num) || num <= 0 || num > 100) return toast.error("Percent discount must be 1-100");
    } else {
      if (Number.isNaN(num) || num <= 0) return toast.error("Flat discount must be greater than 0");
    }
    const payload = {
      discount: form.discount_type === "percent" ? num / 100 : num,
      discount_type: form.discount_type,
      scope: form.scope,
      description: form.description || null,
      active: form.active,
      expires_at: form.expires_at || null,
      max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
    };
    if (!editing) {
      if (!form.code.trim()) return toast.error("Code is required");
      payload.code = form.code.trim().toUpperCase();
    }

    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/promo-codes/${editing.id}`, payload);
        toast.success(`Updated ${editing.code}`);
      } else {
        await api.post("/admin/promo-codes", payload);
        toast.success(`Created ${payload.code}`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="promo-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-[#262626]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">{editing ? "Edit" : "New"}</div>
            <div className="font-display text-xl mt-1">{editing ? `Edit ${editing.code}` : "Create promo code"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {isLegacy && (
            <div className="border border-[#FF2D2D]/30 bg-[#FF2D2D]/5 p-3 text-xs text-[#a3a3a3]" data-testid="legacy-notice">
              <span className="text-white font-semibold">Built-in code.</span> This is a hardcoded
              promo defined in source. You can review its terms and redemption history but it
              can't be edited or disabled from the dashboard.
            </div>
          )}
          {!editing && (
            <Field label="Code" required>
              <input
                type="text"
                required
                value={form.code}
                onChange={(e) => set("code", e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
                data-testid="promo-form-code"
                placeholder="SUMMER20"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white font-mono uppercase focus:border-[#FF2D2D] outline-none"
              />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Discount type" required>
              <select
                value={form.discount_type}
                onChange={(e) => set("discount_type", e.target.value)}
                disabled={isLegacy}
                data-testid="promo-form-type"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="percent">Percent off (%)</option>
                <option value="flat">Flat amount off ($)</option>
              </select>
            </Field>
            <Field label={form.discount_type === "flat" ? "Amount off ($)" : "Discount %"} required>
              <div className="relative">
                {form.discount_type === "flat" && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3] text-sm">$</span>
                )}
                <input
                  type="number"
                  required
                  min={form.discount_type === "flat" ? "0.01" : "1"}
                  max={form.discount_type === "flat" ? undefined : "100"}
                  step={form.discount_type === "flat" ? "0.01" : "1"}
                  value={form.discount}
                  onChange={(e) => set("discount", e.target.value)}
                  disabled={isLegacy}
                  data-testid="promo-form-discount"
                  className={`w-full bg-[#050505] border border-[#262626] py-2 text-white tabular-nums focus:border-[#FF2D2D] outline-none disabled:opacity-60 ${form.discount_type === "flat" ? "pl-7 pr-3" : "px-3"}`}
                />
              </div>
            </Field>
          </div>

          <Field label="Applies to" required>
            <select
              value={form.scope}
              onChange={(e) => set("scope", e.target.value)}
              disabled={isLegacy}
              data-testid="promo-form-scope"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none disabled:opacity-60"
            >
              {scopes.map((s) => (
                <option key={s} value={s}>{SCOPE_LABELS[s] || s}</option>
              ))}
            </select>
          </Field>

          <Field label="Description (optional)" hint="Internal — for your reference">
            <input
              type="text"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              disabled={isLegacy}
              data-testid="promo-form-description"
              placeholder="e.g. Spring campaign 2026"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none disabled:opacity-60"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Expires (optional)" hint="Blank = never">
              <input
                type="date"
                value={form.expires_at}
                onChange={(e) => set("expires_at", e.target.value)}
                disabled={isLegacy}
                data-testid="promo-form-expires"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none disabled:opacity-60"
              />
            </Field>
            <Field label="Max redemptions" hint="Blank = unlimited">
              <input
                type="number"
                min="1"
                step="1"
                value={form.max_uses}
                onChange={(e) => set("max_uses", e.target.value)}
                disabled={isLegacy}
                data-testid="promo-form-max-uses"
                placeholder="unlimited"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white tabular-nums focus:border-[#FF2D2D] outline-none disabled:opacity-60"
              />
            </Field>
          </div>

          <label className={`flex items-center gap-2 text-sm ${isLegacy ? "opacity-60" : "cursor-pointer"}`}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set("active", e.target.checked)}
              disabled={isLegacy}
              data-testid="promo-form-active"
              className="accent-[#FF2D2D]"
            />
            Active
          </label>

          {editing && <RedemptionsPanel data={redemptions} />}

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">
              {isLegacy ? "Close" : "Cancel"}
            </button>
            {!isLegacy && (
              <button
                type="submit"
                disabled={saving}
                data-testid="promo-form-submit"
                className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Create code"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function RedemptionsPanel({ data }) {
  if (!data) {
    return (
      <div className="border-t border-[#262626] pt-4 text-xs text-[#a3a3a3]">Loading redemptions…</div>
    );
  }
  const items = data.redemptions || [];
  return (
    <div className="border-t border-[#262626] pt-4" data-testid="redemptions-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Recent redemptions</div>
        {data.total_discounted_completed > 0 && (
          <div className="text-xs text-[#a3a3a3]">
            <span className="text-white font-semibold tabular-nums">
              ${data.total_discounted_completed.toFixed(2)}
            </span>
            {" "}discounted (completed)
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#a3a3a3] py-4 border border-[#262626] bg-[#050505] text-center">
          No redemptions yet.
        </div>
      ) : (
        <div className="divide-y divide-[#262626] border border-[#262626] bg-[#050505]" data-testid="redemptions-list">
          {items.map((r) => (
            <div key={r.id} className="px-3 py-2.5 grid grid-cols-12 gap-2 text-xs items-center">
              <div className="col-span-4 min-w-0">
                <div className="text-white truncate">{r.client_name || r.client_email || "—"}</div>
                {r.project_title && (
                  <div className="text-[#a3a3a3] truncate">{r.project_title}</div>
                )}
              </div>
              <div className="col-span-3 text-[#a3a3a3] capitalize">
                {(r.type || "").replace(/_/g, " ")}
              </div>
              <div className="col-span-3 tabular-nums">
                <span className="text-[#FF2D2D] font-semibold">
                  -${r.discounted_amount.toFixed(2)}
                </span>
                <span className="text-[#666] text-[10px] ml-1">of ${r.original_amount.toFixed(2)}</span>
              </div>
              <div className="col-span-2 text-right">
                <div className="text-[#a3a3a3] text-[10px]">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                </div>
                <div className={`text-[10px] uppercase tracking-wider ${r.status === "complete" || r.payment_status === "paid" ? "text-[#FF2D2D]" : "text-[#666]"}`}>
                  {r.status === "complete" || r.payment_status === "paid" ? "Paid" : r.status || "pending"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
        {label} {required && <span className="text-[#FF2D2D]">*</span>}
      </label>
      {children}
      {hint && <div className="text-[10px] text-[#666] mt-1">{hint}</div>}
    </div>
  );
}

/**
 * 30-day redemption sparkline. Pure SVG, no deps.
 * Renders a tiny area chart so admins can spot codes gaining traction at a glance.
 */
function Sparkline({ values, testId }) {
  const data = Array.isArray(values) ? values : [];
  const total = data.reduce((a, b) => a + b, 0);

  if (data.length === 0 || total === 0) {
    return (
      <div className="flex items-center gap-1.5 h-5" data-testid={testId} title="No redemptions in the last 30 days">
        <svg width="80" height="14" viewBox="0 0 80 14" className="block">
          <line x1="0" y1="13" x2="80" y2="13" stroke="#262626" strokeWidth="1" strokeDasharray="2 2" />
        </svg>
        <span className="text-[9px] uppercase tracking-wider text-[#666]">No use</span>
      </div>
    );
  }

  const W = 80;
  const H = 14;
  const max = Math.max(...data, 1);
  const step = data.length > 1 ? W / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = data.length === 1 ? W / 2 : i * step;
    const y = H - 1 - (v / max) * (H - 2);
    return [x, y];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${W.toFixed(1)},${H} L0,${H} Z`;
  const peakIdx = data.indexOf(max);
  const [px, py] = points[peakIdx];

  return (
    <div className="flex items-center gap-1.5 h-5" data-testid={testId} title={`${total} redemption${total === 1 ? "" : "s"} in the last 30 days (peak ${max})`}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block">
        <path d={areaPath} fill="#FF2D2D" fillOpacity="0.18" />
        <path d={linePath} fill="none" stroke="#FF2D2D" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={px} cy={py} r="1.6" fill="#FF2D2D" />
      </svg>
      <span className="text-[9px] uppercase tracking-wider text-[#a3a3a3] tabular-nums">
        {total}<span className="text-[#666] ml-0.5">/30d</span>
      </span>
    </div>
  );
}
